import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTranslate } from '@repo/unicity'

export interface CreatedAt {
    dateUTC: string
    fullDateTime: string
    date: string
    time: string
    timezoneCode: string
    timezone: string
}

export interface TicketData {
    reference_no: string
    payment_status: string
    items: { item_code: string; qty: number }[]
    amount: number
    ticket_type: string
    dist_id: string
    dist_phone: string
    dist_email: string
    zone: string
    country: string
    created_at?: CreatedAt
}

/**
 * Format date time: "23 Jan 2026, 03:16 PM (Bangkok)"
 * Use dateUTC to parse and format
 * Remove "Asia/" prefix from timezone
 */
const formatDateTime = (createdAt?: CreatedAt): string => {
    if (!createdAt) return '-'

    const { dateUTC, time, timezone } = createdAt

    // Parse dateUTC and format as "23 Jan 2026"
    const dateObj = new Date(dateUTC)
    const day = dateObj.getDate()
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const month = monthNames[dateObj.getMonth()]
    const year = dateObj.getFullYear()

    // Remove "Asia/" or other region prefixes from timezone
    const cleanTimezone = timezone.replace(/^[A-Za-z]+\//, '')

    return `${day} ${month} ${year}, ${time} (${cleanTimezone})`
}

interface TicketLayoutProps {
    title: string
    description?: string
    data?: TicketData
    isLoading?: boolean
    isSuccess?: boolean
}

const TicketDivider = ({ bgColor = '#ffffff' }: { bgColor?: string }) => {
    const notchSize = 12

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                height: notchSize * 2,
                background: `
                    radial-gradient(circle at 0% 50%, transparent ${notchSize}px, ${bgColor} ${notchSize}px),
                    radial-gradient(circle at 100% 50%, transparent ${notchSize}px, ${bgColor} ${notchSize}px)
                `,
                backgroundSize: '50% 100%',
                backgroundPosition: 'left, right',
                backgroundRepeat: 'no-repeat'
            }}>
            <Box
                sx={{
                    position: 'absolute',
                    left: notchSize + 4,
                    right: notchSize + 4,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    borderTop: '2px dashed',
                    borderColor: 'grey.500'
                }}
            />
        </Box>
    )
}

const InfoRow = ({ label, value, bold = false }: { label: string; value: string; bold?: boolean }) => (
    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'flex-start'} display={'grid'} gridTemplateColumns={'1fr 2fr'}>
        <Typography variant="body2" fontWeight={bold ? 700 : 400} color="primary.main" textAlign={'left'}>
            {label}
        </Typography>
        <Typography variant="body2" fontWeight={bold ? 700 : 400} color="primary.main" textAlign={'right'}>
            {value}
        </Typography>
    </Stack>
)

const TicketLayout = ({ title, description, data, isLoading, isSuccess = true }: TicketLayoutProps) => {
    const { translate } = useTranslate()
    const titleColor = isSuccess ? 'success.main' : 'error.main'

    if (isLoading) {
        return (
            <Stack width={'100%'} maxWidth={'388px'} alignItems="center" py={4}>
                <CircularProgress />
            </Stack>
        )
    }

    return (
        <Stack
            width={'100%'}
            maxWidth={'388px'}
            sx={{
                filter: 'drop-shadow(0px 1px 3px rgba(14, 35, 56, 0.2))'
            }}>
            {/* Header Section */}
            <Stack
                p={'30px 24px'}
                sx={{
                    borderTopLeftRadius: '10px',
                    borderTopRightRadius: '10px',
                    paddingBottom: '16px'
                }}
                bgcolor={'secondary.main'}
                gap={2}>
                <Typography variant="h2" fontSize={'28px'} color={titleColor} fontWeight={500}>
                    {title}
                </Typography>
                {description && (
                    <Typography variant="body2" color={titleColor}>
                        {description}
                    </Typography>
                )}
                {isSuccess && (
                    <Stack gap={1}>
                        <InfoRow label={translate('order_number')} value={data?.reference_no || '-'} />
                        <InfoRow label={translate('ticket_type')} value={data?.ticket_type || '-'} />
                    </Stack>
                )}
            </Stack>

            <TicketDivider />

            {/* Details Section */}
            <Stack
                p={'30px 24px'}
                sx={{
                    borderBottomLeftRadius: '10px',
                    borderBottomRightRadius: '10px',
                    paddingBottom: '30px',
                    paddingTop: '16px'
                }}
                bgcolor={'secondary.main'}
                gap={2}>
                <Stack gap={1}>
                    <InfoRow label={translate('unicity_id')} value={data?.dist_id || '-'} />
                    {isSuccess ? (
                        <>
                            <InfoRow label={translate('email')} value={data?.dist_email || '-'} />
                            <InfoRow label={translate('seat_zone')} value={data?.zone || '-'} />
                            <InfoRow label={translate('amount')} value={data ? `$${data.amount}` : '-'} bold />
                        </>
                    ) : (
                        <>
                            <InfoRow label={translate('reference_id')} value={data?.reference_no || '-'} />
                            <InfoRow label={translate('date_and_time')} value={formatDateTime(data?.created_at)} />
                        </>
                    )}
                </Stack>
                {!isSuccess && (
                    <Stack>
                        <Typography
                            variant="body2"
                            color="rgba(90, 143, 195, 1)"
                            fontSize={'20px'}
                            sx={{ textDecoration: 'underline', textUnderlineOffset: '4px' }}
                            onClick={() => {
                                window.location.href = 'https://glic.unicity.com/'
                            }}>
                            {translate('retry')}
                        </Typography>
                    </Stack>
                )}
            </Stack>
        </Stack>
    )
}

export default TicketLayout
